import { Box, Flex } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/id';

import { ChevronRightIcon, AlertOutlineIcon, UploadIcon, PlayIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import validateClockOut from 'Helper/validateClockOut';
import ButtonSolid from 'UiKit/button/buttonSolid';
import { TextRegular, TextSemiBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonLink from 'UiKit/button/buttonLink';
import getClockInDisabledState from 'View/host/helper/getClockInDisabledState';
import useClockOutConfirmDrawer from 'View/host/hook/useClockOutConfirmDrawer';

import styles from './styles.module.css';

const AttendanceOverview = ({ children, presenceData }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation('host-home');
  const { onClockOutConfirmDrawer } = useClockOutConfirmDrawer();

  const { isLivestreaming, streamingSessions, clockInTime, isTaskSubmitted, isClockInToday, isClockInTomorrow, isClockedIn } = presenceData ?? {};

  const isAllLivestreamDone = streamingSessions?.length === 3;

  const isWorkDone = isTaskSubmitted && isAllLivestreamDone;
  const isClockOutValid = isClockedIn && validateClockOut(clockInTime);

  const clockInDisabledReason = getClockInDisabledState(isClockInToday, isClockInTomorrow);
  const isClockInDisabled = !!clockInDisabledReason;

  const currentDate = moment().locale(i18n.language).format('dddd, DD MMMM YYYY');
  const workDate = moment(presenceData?.workDate).locale(i18n.language).format('dddd, DD MMMM YYYY');
  const buttonText = isClockedIn ? t('attendance.clock_out') : t('attendance.clock_in');
  const overviewDesc = !isClockedIn ? (clockInDisabledReason ? t(clockInDisabledReason) : t('attendance.clock_in_desc')) : null;

  const isHomePage = pathname === '/host';

  const handleClockIn = () => {
    navigate('/host/attendance-record');
  };

  const handleClockOut = () => {
    if (!isClockOutValid || !isAllLivestreamDone) {
      onClockOutConfirmDrawer({
        onConfirm: () => navigate('/host/attendance-record'),
      });
    } else {
      navigate('/host/attendance-record');
    }
  };

  return (
    <Box className={styles['attendance-overview__container']}>
      <Box className={styles['attendance-overview__header']} onClick={() => isHomePage && navigate('/host/presence-content')}>
        <Box>
          <TextRegular className={styles['attendance-overview__shift-label--text']}>{t('attendance.shift_for')}</TextRegular>
          <TextSemiBold className={styles['attendance-overview__shift-value--text']}>{!isClockedIn ? currentDate : workDate}</TextSemiBold>
        </Box>
        <Condition statement={isHomePage}>
          <ChevronRightIcon size={16} />
        </Condition>
      </Box>
      <Box className={styles['attendance-overview__content']}>
        <Condition statement={isClockedIn}>
          <Box className={`${styles['clock-io-time__section']} ${!isLivestreaming && styles['clock-io-time__section--not-live']}`}>{children}</Box>
        </Condition>
        <Condition statement={!isClockedIn}>
          <ButtonSolid className={styles['clock-in__button']} onClick={handleClockIn} disabled={isClockInDisabled}>
            {t('attendance.clock_in')}
          </ButtonSolid>
        </Condition>
        <Condition statement={!isLivestreaming && isClockedIn}>
          <Flex marginTop="12px">
            <Condition statement={!isTaskSubmitted}>
              <ButtonLink flex={1} gap="10px" onClick={() => navigate('/host/task-submission')}>
                <UploadIcon size={18} />
                {t('attendance.content')}
              </ButtonLink>
            </Condition>
            <Condition statement={!isWorkDone}>
              <ButtonOutline className={styles['clock-out__button']} onClick={handleClockOut}>
                {buttonText}
              </ButtonOutline>
            </Condition>
            <Condition statement={isWorkDone}>
              <ButtonSolid
                background="var(--color-warning50)"
                color="var(--color-semantic-text)"
                className={styles['clock-out__button']}
                onClick={handleClockOut}
              >
                {t('attendance.clock_out')}
              </ButtonSolid>
            </Condition>
          </Flex>
        </Condition>
        <Condition statement={isClockedIn && !isLivestreaming && !isAllLivestreamDone}>
          <ButtonSolid background="var(--color-success60)" gap="10px" marginTop="12px" onClick={() => navigate('/host/stream')}>
            <PlayIcon size={18} marginTop="2px" />
            {t('attendance.start_live')}
          </ButtonSolid>
        </Condition>
        <Condition statement={overviewDesc}>
          <Flex marginTop="12px" gap="4px" color="var(--color-primary70)">
            <AlertOutlineIcon size={16} />
            <TextRegular color="var(--color-primary70)">{overviewDesc}</TextRegular>
          </Flex>
        </Condition>
      </Box>
    </Box>
  );
};

export default AttendanceOverview;
